/* Header search -> Diamonds page (#410).
   Shopify's search only sees stones that are already Shopify products; the full
   catalog is in Algolia behind the Diamonds page. Diamond-type searches (a shape
   we stock, a carat, a clarity grade, Hearts & Arrows, a certificate number, or
   just "diamonds") go to /collections/diamonds, which runs them through its own
   search (?find=). Everything else (pages, jewelry, stones we don't carry) keeps
   Shopify's search, including the emerald/ruby notes in search-results.liquid.
   The drop-down has two sections: Diamonds from arelia_loose_diamonds, and Pages
   from Shopify's search, which returns no products (#414). Other words also try
   Algolia as text, so typos find stones and site words simply show Pages.
   A diamond query loaded on the search results page goes to the Diamonds page
   too (direct links, back button); that page lists content only (#413). */
(function () {
  const DIAMONDS = '/collections/diamonds';
  const ID = /^\s*(?:[a-z]{2,5}\s*[:#]?\s*)?\d{6,}\s*$/i;
  const SHAPE = /\b(round|rounds|brilliant|oval|ovals|cushion|cushions|pear|pears|teardrop|princess|princesses|radiant|radiants|marquise|marquises|marquis)\b/;
  const STONE = /\b\d+(?:\.\d+)?\s*(?:ct|cts|carats?)\b|\b(?:vvs1|vvs2|vs1|vs2|fl)\b|\bhearts?\s*(?:&|and|n)\s*arrows?\b|\bh\s*&\s*a\b/;
  const DIAMOND = /\bdiamonds?\b/;
  const KEEP_SHOPIFY = /\b(emeralds?|asschers?|ruby|rubies|sapphires?|moissanites?|faq|guide|shipping|returns?|refund|policy|privacy|terms|contact|about|match|certification|what|how|why|rings?|settings?|earrings?|necklaces?|pendants?|bracelets?|jewelry|jewellery)\b/;

  // URL on the Diamonds page for this search, or null to keep Shopify's search.
  function diamondUrl(raw) {
    const q = String(raw || '').trim();
    const t = q.toLowerCase();
    if (!t || KEEP_SHOPIFY.test(t)) return null;
    if (ID.test(t) || SHAPE.test(t) || STONE.test(t)) return DIAMONDS + '?catalog=all&find=' + encodeURIComponent(q);
    if (DIAMOND.test(t)) return DIAMONDS + '?catalog=all';
    return null;
  }

  // Worth asking Algolia: a diamond query, or any other word that isn't a site topic.
  function catalogQuery(raw) {
    const t = String(raw || '').trim().toLowerCase();
    if (!t || KEEP_SHOPIFY.test(t)) return false;
    return !!diamondUrl(t) || t.length >= 3;
  }

  const SHAPE_WORDS = {
    round: 'Round', rounds: 'Round', brilliant: 'Round',
    oval: 'Oval', ovals: 'Oval',
    cushion: 'Cushion', cushions: 'Cushion',
    pear: 'Pear', pears: 'Pear', teardrop: 'Pear',
    princess: 'Princess', princesses: 'Princess',
    radiant: 'Radiant', radiants: 'Radiant',
    marquise: 'Marquise', marquises: 'Marquise', marquis: 'Marquise',
  };
  const COLOR = ['M', 'L', 'K', 'J', 'I', 'H', 'G', 'F', 'E', 'D'];
  const CLARITY = ['I2', 'I1', 'SI3', 'SI2', 'SI1', 'VS2', 'VS1', 'VVS2', 'VVS1', 'IF', 'FL'];

  function rank(scale, label) {
    const i = scale.indexOf(String(label || '').toUpperCase());
    return i < 0 ? 0 : i + 1;
  }

  // Same axes the Diamonds page applies for a typed search: shape, carat,
  // clarity, color, price cap, Hearts & Arrows. Identifier text is a cert lookup.
  function catalogBody(raw) {
    const q = String(raw || '').trim();
    const body = {
      query: '',
      hitsPerPage: 4,
      filters: 'listed:true AND available:true AND color_rank >= 8 AND clarity_rank >= 8',  // D-F, VVS2+ only, as on the Diamonds page (#412)
      attributesToRetrieve: ['handle', 'title', 'shape', 'carat', 'color', 'clarity', 'cut', 'price', 'image_url', 'url_path'],
    };
    if (ID.test(q)) {
      body.query = q.replace(/^[a-z]{2,5}\s*[:#]?\s*/i, '').trim() || q;
      body.restrictSearchableAttributes = ['sku', 'certificate_number'];
      body.typoTolerance = false;
      body.allowTyposOnNumericTokens = false;
      body.removeWordsIfNoResults = 'none';
      return body;
    }
    const text = (' ' + q.toLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ') + ' ');
    const shapes = [];
    text.replace(/[a-z][a-z'-]*/g, (word) => {
      const shape = SHAPE_WORDS[word];
      if (shape && shapes.indexOf(shape) < 0) shapes.push(shape);
      return word;
    });
    const facetFilters = [];
    if (shapes.length) facetFilters.push(shapes.map((shape) => 'shape:' + shape));
    if (/\bhearts?\s*(?:&|and|n)\s*arrows?\b|\bh\s*&\s*a\b|\bh&a\b/.test(text)) {
      facetFilters.push(['is_hearts_and_arrows:true']);
    }
    const numeric = [];
    const caratRange = text.match(/\b(\d+(?:\.\d+)?)\s*(?:-|to)\s*(\d+(?:\.\d+)?)\s*(?:ct|cts|carats?)\b/);
    const caratUnder = text.match(/\b(?:under|below|less than|up to|max(?:imum)?)\s*(\d+(?:\.\d+)?)\s*(?:ct|cts|carats?)\b/);
    const caratOver = text.match(/\b(?:over|above|more than|at least|min(?:imum)?)\s*(\d+(?:\.\d+)?)\s*(?:ct|cts|carats?)\b/);
    const caratOne = text.match(/\b(\d+(?:\.\d+)?)\s*(?:ct|cts|carats?)\b/);
    if (caratRange) {
      numeric.push('carat>=' + caratRange[1], 'carat<=' + caratRange[2]);
    } else if (caratUnder) {
      numeric.push('carat<=' + caratUnder[1]);
    } else if (caratOver) {
      numeric.push('carat>=' + caratOver[1]);
    } else if (caratOne) {
      const carat = parseFloat(caratOne[1]);
      if (carat > 0 && carat < 20) {
        const tol = carat >= 2 ? 0.15 : 0.1;
        numeric.push('carat>=' + (carat - tol).toFixed(2), 'carat<=' + (carat + tol).toFixed(2));
      }
    }
    const clarity = text.match(/\b(vvs1|vvs2|vs1|vs2|si1|si2|fl|if)\b/);
    if (clarity) {
      const r = rank(CLARITY, clarity[1]);
      if (r) numeric.push('clarity_rank>=' + r, 'clarity_rank<=' + r);
    } else if (/\bvvs\b/.test(text)) {
      numeric.push('clarity_rank>=' + rank(CLARITY, 'VVS2'), 'clarity_rank<=' + rank(CLARITY, 'VVS1'));
    }
    const color = text.match(/\b([d-f])\b/);
    if (color) {
      const r = rank(COLOR, color[1]);
      if (r) numeric.push('color_rank>=' + r, 'color_rank<=' + r);
    }
    const price = text.match(/\b(?:under|below|less than|up to|max(?:imum)?|no more than)\s*\$?\s*(\d[\d,]*(?:\.\d+)?)\s*(k)?\b/);
    if (price && (price[2] || /\$/.test(price[0]) || parseFloat(price[1].replace(/,/g, '')) >= 100)) {
      const cap = Math.round(parseFloat(price[1].replace(/,/g, '')) * (price[2] ? 1000 : 1));
      if (cap > 0) numeric.push('price<=' + cap);
    }
    if (facetFilters.length) body.facetFilters = facetFilters;
    if (numeric.length) body.numericFilters = numeric;
    if (!diamondUrl(q)) {
      body.query = q;
      body.minWordSizefor1Typo = 3;
      body.removeWordsIfNoResults = 'none';
    }
    return body;
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[ch]));
  }

  function stoneHref(hit) {
    const path = String((hit && hit.url_path) || '');
    if (path.charAt(0) === '/' && path.charAt(1) !== '/') return path;
    const handle = String((hit && hit.handle) || '');
    if (/^[a-z0-9-]+$/i.test(handle)) return '/a/diamonds/' + handle;
    return '/collections/diamonds';
  }

  function hitHtml(hit) {
    const title = (hit.carat != null && hit.carat !== '' ? hit.carat + 'ct ' : '') + (hit.shape || 'Diamond');
    const meta = [hit.color, hit.clarity, hit.cut].filter(Boolean).join(' · ');
    const price = '$' + Math.round(Number(hit.price) || 0).toLocaleString('en-US');
    const image = /^https:\/\//.test(hit.image_url || '') ? hit.image_url : '';
    return '<a class="arelia-diamond-hit" href="' + esc(stoneHref(hit)) + '">' +
      (image ? '<img src="' + esc(image) + '" alt="' + esc(title) + '" width="160" height="160">' : '') +
      '<span class="arelia-diamond-hit__title">' + esc(title) + '</span>' +
      (meta ? '<span class="arelia-diamond-hit__meta">' + esc(meta) + '</span>' : '') +
      '<span class="arelia-diamond-hit__price">' + esc(price) + '</span></a>';
  }

  function renderHits(box, data) {
    const hits = (data && data.hits) || [];
    const count = Number(data && data.nbHits) || 0;
    if (!count || !hits.length) {
      box.innerHTML = '<h4 class="predictive-search-results__title">Diamonds</h4>' +
        '<p class="arelia-diamond-hits__count">No diamonds match that in the catalog.</p>';
      return;
    }
    const label = count.toLocaleString('en-US') + (count === 1 ? ' diamond' : ' diamonds') + ' in the catalog';
    box.innerHTML = '<h4 class="predictive-search-results__title">Diamonds</h4>' +
      '<p class="arelia-diamond-hits__count">' + esc(label) + '</p>' +
      '<div class="arelia-diamond-hits__grid">' + hits.map(hitHtml).join('') + '</div>';
  }

  let fetchSeq = 0;
  let fetchCtrl = null;
  let fetchTimer = null;
  let textHit = null;  // a non-diamond word that found stones: { q, url }

  // Where Enter or the row goes for these words, or null to keep Shopify's search.
  function targetUrl(raw) {
    const url = diamondUrl(raw);
    if (url) return url;
    return textHit && textHit.q === String(raw || '').trim() ? textHit.url : null;
  }

  // A non-diamond word found stones: open the section and point the row at them.
  function showTextHits(scope, box, raw, data) {
    const q = String(raw || '').trim();
    if (!data || !data.nbHits) {
      clearCatalog(scope);
      return;
    }
    // A typo the Diamonds page can't read: send the shape Algolia settled on.
    const shapes = (data.hits || []).map((hit) => hit.shape).filter(Boolean);
    const one = shapes.length && shapes.every((shape) => shape === shapes[0]);
    textHit = { q, url: DIAMONDS + '?catalog=all' + (one ? '&find=' + encodeURIComponent(shapes[0].toLowerCase()) : '') };
    scope.classList.add('arelia-catalog-active');
    box.hidden = false;
    renderHits(box, data);
    const row = scope.querySelector('[data-arelia-diamond-row]');
    if (row) {
      row.href = textHit.url;
      const term = row.querySelector('[data-arelia-diamond-term]');
      if (term) term.textContent = q;
      row.hidden = false;
    }
  }

  function clearCatalog(scope) {
    fetchSeq += 1;
    textHit = null;
    clearTimeout(fetchTimer);
    if (fetchCtrl) fetchCtrl.abort();
    if (!scope || !scope.classList) return;
    scope.classList.remove('arelia-catalog-active');
    const box = scope.querySelector('[data-arelia-diamond-hits]');
    if (!box) return;
    box.hidden = true;
    box.innerHTML = '';
  }

  function loadCatalog(scope, raw) {
    const box = scope && scope.querySelector && scope.querySelector('[data-arelia-diamond-hits]');
    if (!box || !catalogQuery(raw)) {
      clearCatalog(scope);
      return;
    }
    const stone = !!diamondUrl(raw);
    const seq = ++fetchSeq;
    if (stone) {
      scope.classList.add('arelia-catalog-active');
      box.hidden = false;
    }
    const key = box.dataset.algoliaKey;
    const app = box.dataset.algoliaApp;
    const index = box.dataset.algoliaIndex || 'arelia_loose_diamonds';
    if (!key || !app) {
      if (stone) box.innerHTML = '<p class="arelia-diamond-hits__count">Open the link above to search the catalog.</p>';
      return;
    }
    if (stone && !box.querySelector('.arelia-diamond-hit')) {
      box.innerHTML = '<p class="arelia-diamond-hits__count">Searching the catalog…</p>';
    }
    if (fetchCtrl) fetchCtrl.abort();
    const ctrl = new AbortController();
    fetchCtrl = ctrl;
    const host = 'https://' + app.toLowerCase() + '-dsn.algolia.net';
    fetch(host + '/1/indexes/' + encodeURIComponent(index) + '/query', {
      method: 'POST',
      signal: ctrl.signal,
      headers: {
        'X-Algolia-API-Key': key,
        'X-Algolia-Application-Id': app,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(catalogBody(raw)),
    }).then((resp) => {
      if (!resp.ok) throw new Error('catalog search failed');
      return resp.json();
    }).then((data) => {
      if (seq !== fetchSeq) return;
      if (stone) renderHits(box, data); else showTextHits(scope, box, raw, data);
    }).catch((err) => {
      if (seq !== fetchSeq || (err && err.name === 'AbortError')) return;
      if (!stone) { clearCatalog(scope); return; }
      box.innerHTML = '<p class="arelia-diamond-hits__count">The catalog didn’t load. The link above still opens every match.</p>';
    });
  }

  function scheduleCatalog(scope, raw) {
    clearTimeout(fetchTimer);
    if (!scope || !scope.querySelector || !scope.querySelector('[data-arelia-diamond-hits]')) return;
    if (!catalogQuery(raw)) {
      clearCatalog(scope);
      return;
    }
    if (diamondUrl(raw)) {
      scope.classList.add('arelia-catalog-active');
      const box = scope.querySelector('[data-arelia-diamond-hits]');
      box.hidden = false;
      if (!box.innerHTML) box.innerHTML = '<p class="arelia-diamond-hits__count">Searching the catalog…</p>';
    }
    fetchTimer = setTimeout(() => loadCatalog(scope, raw), 200);
  }

  function updateRow(row, raw) {
    const url = diamondUrl(raw);
    if (!url) { row.hidden = true; return; }
    row.href = url;
    const term = row.querySelector('[data-arelia-diamond-term]');
    if (term) term.textContent = String(raw).trim();
    row.hidden = false;
  }

  // Drop-down row follows the typed text.
  document.addEventListener('input', (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'q') return;
    const component = input.closest('predictive-search-component');
    const scope = component || document;
    scope.querySelectorAll('[data-arelia-diamond-row]').forEach((row) => updateRow(row, input.value));
    if (component) scheduleCatalog(component, input.value);
  });

  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest && e.target.closest('.predictive-search__reset-button');
    if (!btn) return;
    clearCatalog(btn.closest('predictive-search-component'));
  }, true);

  document.addEventListener('dialog:close', (e) => {
    const root = e.target;
    const scope = root && root.querySelector && root.querySelector('predictive-search-component');
    if (scope) clearCatalog(scope);
  });

  // Enter in the search box, unless a Pages result is highlighted. With the
  // Diamonds section open, arrow keys stay put and Enter opens the stones.
  window.addEventListener('keydown', (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'q') return;
    const scope = input.closest('predictive-search-component');
    const catalog = scope && scope.classList.contains('arelia-catalog-active');
    if (catalog && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key !== 'Enter') return;
    if (!catalog && scope && scope.querySelector('[aria-selected="true"]')) return;
    const url = targetUrl(input.value);
    if (!url) return;
    e.preventDefault();
    e.stopPropagation();
    window.location.href = url;
  }, true);

  // "View all" button and any other search form submit.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !/\/search\/?$/.test(new URL(form.action, location.origin).pathname)) return;
    const q = form.querySelector('input[name="q"]');
    const url = q && targetUrl(q.value);
    if (!url) return;
    e.preventDefault();
    window.location.href = url;
  }, true);

  // Search results page: Shopify's search can't see the catalog, so a diamond
  // query that lands here by any route moves to the Diamonds page.
  if (/^\/search\/?$/.test(location.pathname)) {
    const url = diamondUrl(new URLSearchParams(location.search).get('q'));
    if (url) location.replace(url);
  }
})();
